import React, { useState } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Alert,
  Box,
  Paper,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

interface PaymentResponse {
  message: string;
  transaction_id?: string;
}

/**
 * Payment component for processing M-Pesa payments
 * Allows users to enter an amount and initiate a payment transaction
 * @returns React component for payment processing
 */
const Payment: React.FC = () => {
  const [amount, setAmount] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  /**
   * Handles the payment form submission
   * Validates the amount, sends payment request to the server, and handles the response
   * @param e - The form submission event
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!amount) {
      setError('Please enter an amount');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch('/payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ amount: parseFloat(amount) })
      });

      const data: PaymentResponse = await response.json();
      
      if (response.status === 200) {
        setError('');
        alert('Payment initiated');
        navigate('/dashboard');
      } else {
        setError(data.message || 'Payment failed');
      }
    } catch (err) {
      setError('An error occurred while processing your payment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom align="center">
            Make a Payment
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <form onSubmit={handleSubmit}>
            <TextField
              name="amount"
              label="Amount (KES)"
              variant="outlined"
              fullWidth
              margin="normal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              type="number"
              required
              disabled={loading}
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {loading ? 'Processing...' : 'Pay with M-Pesa'}
            </Button>
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

export default Payment;